/**
 * Mission Create Search Context Slice (GW-8289)
 *
 * Holds the gw-search-api recommendation/entity-extraction fetch that backs
 * the rebuilt /marketplace/missions/:missionTypeId/create page: dataset
 * recommendations (results[], narrowed to datasets server-side via the
 * `type: ['dataset']` filter — GW-9535/GW-8764) and detected entities
 * (entities[]/entitiesAvailable), keyed off whatever prompt text carried over
 * from the originating search context (or whatever the user types into the
 * page's own Prompt section).
 *
 * Deliberately separate from `missionBriefingSlice` (GW-7870): that slice is
 * the persisted MissionBriefing draft itself (subject/scope/datasets/budget
 * edits, survives a refresh via store.ts partialize). This slice is the
 * ephemeral fetch status + results for populating that draft from
 * gw-search-api — it is NOT persisted (recomputed on demand from the current
 * prompt) and clears whenever the create flow is abandoned or completes,
 * same as the briefing draft it feeds.
 */

import { searchApi } from '@/api/searchApi';
import type {
  Direction,
  SearchEntity,
  SearchResponse,
  SearchResult,
} from '@/types/searchResponse.types';

import type { StateCreator } from 'zustand';

type MissionCreateSearchContextStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Optional ranking signals (GW-9535) forwarded to gw-search-api alongside the
 * dataset recommendation fetch: the current mission type and the canonicalIds
 * of the briefing's identified entities. Both are omitted from the request
 * when absent.
 */
export interface MissionCreateRankingSignals {
  missionType?: string;
  acceptedEntityIds?: string[];
}

export interface MissionCreateSearchContextSlice {
  missionCreateSearchStatus: MissionCreateSearchContextStatus;
  missionCreateRecommendedDatasets: SearchResult[];
  missionCreateDetectedEntities: SearchEntity[];
  missionCreateEntitiesAvailable: boolean;
  /**
   * GW-10593: per-entity hydrated DirectionTemplates from the on-load
   * gw-search-api response (`SearchResponse.directions[]`), captured
   * alongside results/entities/entitiesAvailable so MissionCreatePage's
   * entity-merge effect can thread them into `mapSearchEntitiesToBriefingEntities`
   * (GW-10591) exactly like SearchNextPage's own on-load fetch already does.
   * Previously discarded at this slice boundary — `response.directions` never
   * made it past `fetchMissionCreateSearchContext` even though the full
   * SearchResponse always carried it. Absent (omitempty on the wire) whenever
   * gw-taxonomy-api's DirectionTemplate loader isn't configured server-side,
   * so this defaults to `[]`, never fabricated.
   */
  missionCreateDirections: Direction[];
  missionCreateSearchError: string | null;
  /**
   * Fetches gw-search-api recommendations/entities for `prompt`, storing the
   * result in this slice. Returns the raw SearchResponse so callers can merge
   * entities into missionBriefingSlice without waiting on a re-render.
   *
   * GW-9535 (consumer side of GW-8764): the request now sends the server-side
   * `type: ['dataset']` item-kind filter — the provider narrows to datasets
   * server-side, so this slice no longer over-fetches every kind and filters
   * client-side. Optional `rankingSignals` (missionType/acceptedEntityIds)
   * bias the provider's ranking toward the current mission type and its
   * identified entities; both are forwarded verbatim when supplied.
   */
  fetchMissionCreateSearchContext: (
    prompt: string,
    rankingSignals?: MissionCreateRankingSignals
  ) => Promise<SearchResponse | null>;
  clearMissionCreateSearchContext: () => void;
}

const initial: Pick<
  MissionCreateSearchContextSlice,
  | 'missionCreateSearchStatus'
  | 'missionCreateRecommendedDatasets'
  | 'missionCreateDetectedEntities'
  | 'missionCreateEntitiesAvailable'
  | 'missionCreateDirections'
  | 'missionCreateSearchError'
> = {
  missionCreateSearchStatus: 'idle',
  missionCreateRecommendedDatasets: [],
  missionCreateDetectedEntities: [],
  missionCreateEntitiesAvailable: false,
  missionCreateDirections: [],
  missionCreateSearchError: null,
};

export const createMissionCreateSearchContextSlice: StateCreator<
  MissionCreateSearchContextSlice,
  [],
  [],
  MissionCreateSearchContextSlice
> = (set) => ({
  ...initial,
  fetchMissionCreateSearchContext: async (
    prompt: string,
    rankingSignals?: MissionCreateRankingSignals
  ) => {
    const trimmed = prompt.trim();
    if (!trimmed) return null;

    // Reset prior results immediately so a re-fetch (e.g. the user edits the
    // prompt) never flashes the previous prompt's stale recommendations/
    // entities while the new request is in flight.
    set({
      missionCreateSearchStatus: 'loading',
      missionCreateRecommendedDatasets: [],
      missionCreateDetectedEntities: [],
      missionCreateEntitiesAvailable: false,
      missionCreateDirections: [],
      missionCreateSearchError: null,
    });
    try {
      // GW-9535: server-side `type: ['dataset']` filter (GW-8764) — the
      // provider narrows results to datasets, so no client-side
      // itemType==='dataset' over-fetch/filter is needed. missionType/
      // acceptedEntityIds bias the provider's ranking when supplied.
      const response = await searchApi.search({
        q: trimmed,
        type: ['dataset'],
        ...(rankingSignals?.missionType ? { missionType: rankingSignals.missionType } : {}),
        ...(rankingSignals?.acceptedEntityIds
          ? { acceptedEntityIds: rankingSignals.acceptedEntityIds }
          : {}),
      });
      set({
        missionCreateSearchStatus: 'ready',
        missionCreateRecommendedDatasets: response.results,
        missionCreateDetectedEntities: response.entities,
        missionCreateEntitiesAvailable: response.entitiesAvailable,
        missionCreateDirections: response.directions ?? [],
        missionCreateSearchError: null,
      });
      return response;
    } catch (err) {
      set({
        missionCreateSearchStatus: 'error',
        missionCreateSearchError:
          err instanceof Error ? err.message : 'Unable to load recommendations for this prompt.',
      });
      return null;
    }
  },
  clearMissionCreateSearchContext: () => set({ ...initial }),
});
